import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, UntypedFormGroup } from '@angular/forms';
import { JournalCurrenciesDrawerCommand } from '@app/shared/models/drawer/Journal';
import { AppSettingsService } from '@app/shared/services/app';
import { JournalCurrenciesDataSharingService } from '@app/shared/services/data-helper';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';

@Component({
    selector: 'app-journal-currencies-info-drawer',
    templateUrl: './journal-currencies-info-drawer.component.html',
    styleUrls: ['./journal-currencies-info-drawer.component.scss'],
    standalone: false
})
export class JournalCurrenciesInfoDrawerComponent implements OnInit {
  @Input() mode: string;

  data: JournalCurrenciesDrawerCommand;

  baseCurrency1: string;
  baseCurrency2: string;
  allowDualCurrency: boolean;
  allowBaseCurrency: boolean;

  form: UntypedFormGroup;

  rateFormatOptions: NumberFormatOptions = {
    style: 'decimal',
    maximumFractionDigits: 4
  };

  constructor(
    private appSettingsService: AppSettingsService,
    private journalCurrenciesDataSharingService: JournalCurrenciesDataSharingService,
    private formBuilder: FormBuilder
  ) {
    this.baseCurrency1 =
      this.appSettingsService.currenciesSettings['baseCurrency'];
    this.baseCurrency2 =
      this.appSettingsService.currenciesSettings['baseCurrency2'];
    this.allowDualCurrency =
      this.appSettingsService.currenciesSettings['allowDualCurrency'];
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      baseAmount1: [null],
      baseAmount2: [null],
      parityRate: [null],
      currencyRate: [null]
    });

    const { baseAmount1, currencyRate, baseAmount2, parityRate } = this.form.controls;
    if (this.mode === 'view') {
      // Disable fields on View screen
      this.disableField(baseAmount1);
      this.disableField(currencyRate);
      this.disableField(baseAmount2);
      this.disableField(parityRate);
    }

    // Update respective record in grid, based on user input in drawer
    Object.keys(this.form.controls).forEach((controlName) => {
      const control = this.form.get(controlName);
      control.valueChanges.subscribe(() => {
        this.journalCurrenciesDataSharingService.setDrawerFormData({
          ...this.form.getRawValue(),
          controlChanged: controlName,
          rowIndex: this.data?.rowIndex
        });
      });
    });

    // Populate drawer data with grid record
    this.journalCurrenciesDataSharingService.data$.subscribe((data) => {
      this.data = {
        rowIndex: data?.rowIndex,
        baseAmount1: data?.baseAmount1 ?? 0,
        baseAmount2: data?.baseAmount2 ?? 0,
        parityRate: data?.parityRate,
        // line item currency
        currencyUid: data?.currencyUid,
        currencyRate: data?.currencyRate,
        amount: data?.amount ?? 0,
        accountDetails: data?.accountDetails
      };

      this.form.patchValue({
        baseAmount1: this.data.baseAmount1,
        baseAmount2: this.data.baseAmount2,
        parityRate: this.data.parityRate,
        currencyRate: this.data.currencyRate
      }, { emitEvent: false });

      const { baseAmount1, baseAmount2 } = this.form.controls;
      // Handle disabling of baseAmounts for dual base currency
      // when on Form screens
      if (data?.currencyUid && this.mode !== 'view' && this.allowDualCurrency) {
        if (data.currencyUid === this.baseCurrency1) {
          this.disableField(baseAmount1);
        } else {
          this.enableField(baseAmount1);
        }

        if (data.currencyUid === this.baseCurrency2) {
          this.disableField(baseAmount2);
        } else {
          this.enableField(baseAmount2);
        }
      }

      // Show first base currency when line item currency is different than base currency
      this.allowBaseCurrency = false;
      if (data?.currencyUid && data?.currencyUid !== this.baseCurrency1) {
        this.allowBaseCurrency = true;
      }
    });

    // Get journal level data to be displayed always
    this.journalCurrenciesDataSharingService.journalLevelData$.subscribe(
      (data) => {
        this.data = {
          ...this.data,
          // For main JV
          unbalancedCredit: data?.unbalancedCredit,
          unbalancedDebit: data?.unbalancedDebit,
          unbalancedCredit2: data?.unbalancedCredit2,
          unbalancedDebit2: data?.unbalancedDebit2
        };
      }
    );
  }

  showBaseAmount() {
    // Show when using dual base currency
    if (this.allowDualCurrency) {
      return true;
    }

    // Show when mono currency is being used AND line item currency is different from base currency
    return this.allowBaseCurrency;
  }

  showAmountAndCurrencyRate() {
    // Show only for dual base currency
    if (!this.data?.currencyUid || !this.allowDualCurrency) {
      return false;
    }

    if (this.allowDualCurrency) {
      // In case of dual base currency, when line item currency is a 3rd kind of currency
      return (
        this.data?.currencyUid !== this.baseCurrency1 &&
        this.data?.currencyUid !== this.baseCurrency2
      );
    }
  }

  private disableField(control: AbstractControl) {
    control.disable({ emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }

  private enableField(control: AbstractControl) {
    control.enable({ emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }

  onChangeBaseAmount1(value: number) {
    if (!this.allowDualCurrency) {
      return;
    }

    const { baseAmount2, parityRate } = this.form.controls;
    // Check if baseAmount1 exists, in order to avoid division by 0 error
    if (!value) {
      parityRate.setValue(0);
      return;
    }

    parityRate.setValue(baseAmount2.value / value);
  }

  onChangeBaseAmount2(value: number) {
    const { baseAmount1, parityRate } = this.form.controls;
    // Check if baseAmount1 exists, in order to avoid division by 0 error
    if (!baseAmount1.value) {
      return;
    }

    parityRate.setValue(value / baseAmount1.value);
  }

  onChangeParityRate(value: number) {
    const { baseAmount1, baseAmount2 } = this.form.controls;
    if (baseAmount2.enabled) {
      baseAmount2.setValue(baseAmount1.value * value);
    }
    else if (baseAmount1.enabled) {
      // Check if parityRate exists, in order to avoid division by 0 error
      if (!value) {
        baseAmount1.setValue(0);
        return;
      }

      baseAmount1.setValue(baseAmount2.value / value);
    }
  }

  hasUnbalancedSides() {
    return this.data.unbalancedCredit > 0 ||
      this.data.unbalancedDebit > 0 ||
      this.data.unbalancedCredit2 > 0 ||
      this.data.unbalancedDebit2 > 0;
  }

  onClickAddBalancingRow() {
    this.journalCurrenciesDataSharingService.triggerBalanceNewRow();
  }
}
